import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { putFile } from '../../api/client';
import { partner, type StepTarget } from '../../api/partner';
import type { ProgressStage, Step, StepKey } from '../../api/types';
import { currentPosition, useSettings } from '../../app/settings';
import { toast } from '../../app/toast';
import { BigButton } from '../../components/BigButton';
import { BodyIcon, BorderIcon, FlagIcon, PinIcon } from '../../components/Icons';
import { PhotoCapture } from '../../components/PhotoCapture';
import { Sheet } from '../../components/Sheet';
import { UploadProgress } from '../../components/UploadProgress';
import { useT, type DictKey } from '../../i18n';
import { describeError } from '../../lib/errors';
import { useFormat } from '../../lib/format';
import { preparePhoto } from '../../lib/image';
import { StepFlow, type StepDeps } from './stepFlow';

export const MAIN_STEPS: { key: StepKey; icon: React.ReactNode }[] = [
  { key: 'picked_up', icon: <BodyIcon /> },
  { key: 'border_crossed', icon: <BorderIcon /> },
  { key: 'delivered', icon: <FlagIcon /> },
];
export const EXTRA_STEPS: StepKey[] = ['customs_cleared', 'other'];

/** Main steps a handover progress stage already implies (stage can be set in the CRM without recording steps). */
export const STAGE_COVERS: Record<ProgressStage, StepKey[]> = {
  accepted: [],
  picked_up: ['picked_up'],
  in_transit: ['picked_up'],
  delivered: ['picked_up', 'border_crossed', 'delivered'],
};

export const stepLabel = (k: string): DictKey => `step.${k}` as DictKey;

const liveDeps: StepDeps = {
  uploadUrl: partner.uploadUrl,
  put: putFile,
  complete: partner.completeDocument,
  addStep: partner.addStep,
  prepare: preparePhoto,
};

function useFlow(flow: StepFlow) {
  return useSyncExternalStore(
    (cb) => flow.subscribe(cb),
    () => flow.snapshot,
  );
}

/** Three big step buttons + «Інше / митниця», and the timeline of steps already made. */
export function StepPanel({
  target,
  steps,
  onChanged,
  disabled,
  stage,
}: {
  target: StepTarget;
  steps: Step[];
  onChanged: () => void;
  disabled?: boolean;
  /** Handover status set without steps (e.g. in the CRM): steps it already covers are not offered as «next». */
  stage?: ProgressStage | null;
}) {
  const t = useT();
  const [open, setOpen] = useState<StepKey | null>(null);
  const [more, setMore] = useState(false);
  const f = useFormat();
  // latest time of each step: done steps turn into light «✓ Зроблено · 12 жовт., 14:05» cards,
  // the first step not done yet is the only dark (primary) button — what to press next is obvious at a glance
  const doneAt = new Map<string, string>();
  for (const s of steps) if (!doneAt.has(s.step_key) || s.at > doneAt.get(s.step_key)!) doneAt.set(s.step_key, s.at);
  const byStatus = new Set<StepKey>(stage ? STAGE_COVERS[stage] : []);
  const next = MAIN_STEPS.find((s) => !doneAt.has(s.key) && !byStatus.has(s.key))?.key ?? null;
  return (
    <section aria-labelledby="steps-h">
      <div className="space-y-3">
        {MAIN_STEPS.map((s) => {
          const at = doneAt.get(s.key);
          // marked by status without a step: say so — the button stays open to add the photo proof
          const hint = at
            ? t('step.doneAt', { at: f.date(at) })
            : byStatus.has(s.key)
              ? t('step.byStatus')
              : !disabled && s.key === next
                ? t('step.next')
                : undefined;
          return (
            <BigButton
              key={s.key}
              icon={s.icon}
              onClick={() => setOpen(s.key)}
              disabled={disabled}
              done={!!at}
              tone={!disabled && s.key === next ? 'primary' : 'plain'}
              hint={hint}
              label={hint ? `${t(stepLabel(s.key))} — ${hint}` : t(stepLabel(s.key))}
            >
              {t(stepLabel(s.key))}
            </BigButton>
          );
        })}
        <button type="button" className="btn w-full" aria-expanded={more} disabled={disabled} onClick={() => setMore((v) => !v)}>
          {t('step.moreMenu')}
        </button>
        {more ? (
          <div className="grid grid-cols-2 gap-3">
            {EXTRA_STEPS.map((k) => (
              <button key={k} type="button" className="btn min-h-big" onClick={() => setOpen(k)}>
                {t(stepLabel(k))}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <h2 id="steps-h" className="sec">
        {t('step.title')}
      </h2>
      <StepTimeline steps={steps} />

      {open ? (
        <StepSheet
          key={open}
          target={target}
          stepKey={open}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null);
            setMore(false);
            onChanged();
          }}
        />
      ) : null}
    </section>
  );
}

export function StepTimeline({ steps }: { steps: Step[] }) {
  const t = useT();
  const f = useFormat();
  if (!steps.length) return <p className="text-[15px] text-lab">{t('step.empty')}</p>;
  const sorted = [...steps].sort((a, b) => b.at.localeCompare(a.at));
  return (
    <ol className="space-y-3" data-testid="steps">
      {sorted.map((s) => (
        <li key={s.id} className="card">
          <div className="flex items-baseline gap-2">
            <span className="min-w-0 flex-1 text-[17px] font-semibold">{t(stepLabel(s.step_key))}</span>
            <time className="text-[14px] text-lab" dateTime={s.at}>
              {f.date(s.at)}
            </time>
          </div>
          {s.actor_name ? <p className="text-[14px] text-lab">{s.actor_name}</p> : null}
          {s.note ? <p className="mt-1 whitespace-pre-line text-[15px]">{s.note}</p> : null}
          {s.lat != null && s.lng != null ? (
            <a
              className="mt-1 inline-flex min-h-tap items-center gap-1 text-[14px] text-txt underline"
              href={`https://www.openstreetmap.org/?mlat=${s.lat}&mlon=${s.lng}#map=12/${s.lat}/${s.lng}`}
              target="_blank"
              rel="noreferrer"
            >
              <PinIcon /> {s.lat.toFixed(4)}, {s.lng.toFixed(4)}
            </a>
          ) : null}
          {s.documents?.length ? (
            <div className="mt-2">
              <p className="text-[14px] text-lab">{t('step.photos', { n: s.documents.length })}</p>
              <div className="mt-1 flex flex-wrap gap-2">
                {s.documents.map((d) => (
                  <PhotoThumb key={d.id} id={d.id} name={d.file_name} mime={d.mime} />
                ))}
              </div>
            </div>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

/** Lazy thumbnail via a presigned URL; falls back to the file name when the file cannot be shown. */
function PhotoThumb({ id, name, mime }: { id: string; name: string; mime: string }) {
  const t = useT();
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    if (!mime.startsWith('image/') || /hei[cf]/i.test(mime)) {
      setFailed(true);
      return;
    }
    partner.documentUrl(id).then(
      (r) => live && setUrl(r.url),
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, [id, mime]);
  const open = async () => {
    try {
      const r = url ? { url } : await partner.documentUrl(id);
      window.open(r.url, '_blank', 'noopener');
    } catch (e) {
      toast(t(describeError(e)), 'bad');
    }
  };
  return (
    <button type="button" onClick={() => void open()} aria-label={`${t('step.openPhoto')}: ${name}`} className="overflow-hidden rounded-xl border border-line">
      {url && !failed ? (
        <img src={url} alt={name} className="h-20 w-20 object-cover" loading="lazy" onError={() => setFailed(true)} />
      ) : (
        <span className="flex h-20 w-20 items-center justify-center break-all p-1 text-center text-[11px] text-lab">{name}</span>
      )}
    </button>
  );
}

export function StepSheet({
  target,
  stepKey,
  onClose,
  onDone,
  deps = liveDeps,
}: {
  target: StepTarget;
  stepKey: StepKey;
  onClose: () => void;
  onDone: () => void;
  deps?: StepDeps;
}) {
  const t = useT();
  const geoOn = useSettings((s) => s.geo);
  // one flow per opened sheet (the parent remounts the sheet per step key)
  const [flow] = useState(() => new StepFlow(deps, target, stepKey));
  useEffect(() => () => flow.dispose(), [flow]);
  const st = useFlow(flow);
  const [note, setNote] = useState('');
  const busy = st.phase === 'uploading' || st.phase === 'posting';

  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  useEffect(() => {
    if (st.phase === 'done') {
      toast(t('step.saved'));
      doneRef.current();
    } else if (st.phase === 'queued') {
      toast(t('step.offline'));
      doneRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st.phase]);

  async function save(confirmNoPhoto = false) {
    if (st.phase === 'upload_failed' || st.phase === 'failed') {
      await flow.retry();
      return;
    }
    const coords = geoOn ? await currentPosition() : null;
    await flow.submit({ note, confirmNoPhoto, coords });
  }

  return (
    <Sheet title={t(stepLabel(stepKey))} onClose={busy ? () => undefined : onClose}>
      <PhotoCapture onPick={(files) => flow.addFiles(files)} disabled={busy || st.phase === 'upload_failed'} />
      <p className="mt-2 text-[13px] text-lab">{t('photo.jpegHint')}</p>
      <UploadProgress files={st.files} onRemove={(k) => flow.removeFile(k)} locked={busy} />
      <div className="mt-4">
        <label htmlFor="step-note" className="label">
          {t('step.note')}
        </label>
        <textarea id="step-note" className="input min-h-[84px]" value={note} maxLength={2000} disabled={busy} onChange={(e) => setNote(e.target.value)} />
      </div>
      {geoOn ? (
        <p className="mt-2 inline-flex items-center gap-1 text-[14px] text-lab">
          <PinIcon /> {t('step.withGps')}
        </p>
      ) : null}
      {st.phase === 'confirm_no_photo' ? (
        <p role="alert" className="mt-4 rounded-xl border border-warn/60 px-4 py-3 text-warn">
          {t('step.noPhotoWarn')}
        </p>
      ) : null}
      {st.phase === 'upload_failed' ? (
        <p role="alert" className="mt-4 text-bad">
          {t('step.failed')}
        </p>
      ) : null}
      {st.phase === 'failed' ? (
        <p role="alert" className="mt-4 text-bad">
          {t(describeError(st.error))}
        </p>
      ) : null}
      <div className="mt-5 space-y-3">
        {st.phase === 'confirm_no_photo' ? (
          <button type="button" className="btn min-h-big w-full" onClick={() => void save(true)}>
            {t('step.saveNoPhoto')}
          </button>
        ) : (
          <button type="button" className="btn-primary min-h-big w-full text-[18px]" disabled={busy} onClick={() => void save()}>
            {busy ? t('step.saving') : st.phase === 'upload_failed' || st.phase === 'failed' ? t('step.retry') : t('step.save')}
          </button>
        )}
      </div>
    </Sheet>
  );
}
